// src/context/uploadPersistence.js
// Tiny IndexedDB wrapper to persist File objects across page reloads.
// localStorage cannot store Blobs, so we use IndexedDB for the actual file bytes,
// and localStorage for the lightweight queue metadata.

const DB_NAME = "uploadManagerDB";
const DB_VERSION = 1;
const STORE_FILES = "files";
const STORE_META = "meta";

const META_KEY = "uploadManager.pendingBatch";

const openDB = () => {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not supported"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_FILES)) {
        db.createObjectStore(STORE_FILES, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

const txPromise = (tx) =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

export const savePendingFiles = async (records) => {
  // records: [{ id, file }]
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_FILES, "readwrite");
    const store = tx.objectStore(STORE_FILES);
    // Clear old entries first
    store.clear();
    records.forEach((r) => store.put({ id: r.id, file: r.file }));
    await txPromise(tx);
    db.close();
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[uploadPersistence] savePendingFiles failed:", e);
    return false;
  }
};

export const loadPendingFiles = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_FILES, "readonly");
    const store = tx.objectStore(STORE_FILES);
    const req = store.getAll();
    const result = await new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return result; // [{ id, file }]
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[uploadPersistence] loadPendingFiles failed:", e);
    return [];
  }
};

export const clearPendingFiles = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_FILES, "readwrite");
    tx.objectStore(STORE_FILES).clear();
    await txPromise(tx);
    db.close();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[uploadPersistence] clearPendingFiles failed:", e);
  }
};

export const savePendingMeta = (meta) => {
  try {
    if (meta == null) {
      localStorage.removeItem(META_KEY);
    } else {
      localStorage.setItem(META_KEY, JSON.stringify(meta));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[uploadPersistence] savePendingMeta failed:", e);
  }
};

export const loadPendingMeta = () => {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearPendingMeta = () => {
  try {
    localStorage.removeItem(META_KEY);
  } catch {
    /* ignore */
  }
};